import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, map, Observable, take } from 'rxjs';
import { Organization, Profile, UpdateProfileRequest, UserService } from './user.service';
import { PatientService } from './patient.service';
import { LocationProvider, Patient, SearchPatientsRequest } from './patient.model';
import { findExactPatientMatch as matchPatientByNameAndPhone } from './patient-match.util';

@Injectable({
  providedIn: 'root',
})
export class ContactsViewModel {
  private profileService = inject(UserService);
  private patientService = inject(PatientService);
  private _loading = new BehaviorSubject<boolean>(true);
  private _patientLoaded = new BehaviorSubject<boolean>(false);

  get profile$(): Observable<Profile | null> {
    return this._profile$.asObservable();
  }

  get currentOrganization$(): Observable<Organization | null> {
    return this._currentOrganization$.asObservable();
  }

  get locations$(): Observable<LocationProvider[]> {
    return this._locations$.asObservable();
  }

  get patients$(): Observable<Patient[]> {
    return this._patients$.asObservable();
  }

  get loading$(): Observable<boolean> {
    return this._loading.asObservable();
  }

  get locationsLoading$(): Observable<boolean> {
    return this._locationsLoading.asObservable();
  }

  get patientLoaded$(): Observable<boolean> {
    return this._patientLoaded.asObservable();
  }

  private _locations$ = new BehaviorSubject<LocationProvider[]>([]);
  private _locationsLoading = new BehaviorSubject<boolean>(true);
  private _patientCache: Patient[] = [];

  private _profile$ = new BehaviorSubject<Profile | null>(null);

  private _currentOrganization$ = new BehaviorSubject<Organization | null>(null);

  private _patients$ = new BehaviorSubject<Patient[]>([]);

  private _profileError$ = new BehaviorSubject<string | null>(null);

  get profileError$(): Observable<string | null> {
    return this._profileError$.asObservable();
  }
  /**
   * Resets all BehaviorSubject properties to their initial states.
   */
  reset(): void {
    this._loading.next(true);
    this._locationsLoading.next(true);
    this._locations$.next([]);
    this._patients$.next([]);
    this._patientCache = [];
  }
  /**
   * Loads the profile data and updates the relevant observables.
   * Also stores the current organization ID in session storage.
   */
  loadProfile(): void {
    this.profileService
      .getProfile()
      .pipe(take(1))
      .subscribe({
        next: (profile) => {
          this._profile$.next(profile);
          sessionStorage.setItem('organizationId', profile.currentOrg);
          this._currentOrganization$.next(
            profile?.organizations.find((org) => org.id === profile.currentOrg) || null,
          );
        },
        error: (err) => {
          console.error('Error loading profile:', err);
          this._profileError$.next('Error loading profile');
        },
      });
  }

  /**
   * Loads the patients based on the provided search criteria.
   * Updates the patients observable and sets the loading state.
   *
   * @param args - The search criteria for loading patients.
   */

  loadPatients(args: SearchPatientsRequest): void {
    this._loading.next(true);
    this._patients$.next([]);
    this.patientService.getPatients(args).subscribe({
      next: ({ result }) => {
        this._patientCache = result;
        this._patients$.next(result);
        this._loading.next(false);
        this._patientLoaded.next(true);
      },
      error: () => {
        this._loading.next(false);
      },
    });
  }

  /**
   * Filters the cached patient list locally (no API). Used by the search box after initial load.
   * Matches patient name always; matches phone only when the term looks like a phone fragment (4+ digits).
   * Location name is excluded — most patients share one callback location (e.g. "DataFinn Main Office"),
   * which falsely matched substrings like "Finn" or "Data".
   */
  filterPatients(search: string): void {
    const term = search.trim().toLowerCase();
    if (!term) {
      this._patients$.next(this._patientCache);
      return;
    }

    const digitTerm = term.replace(/\D/g, '');
    const matchPhone = digitTerm.length >= 4;

    const filtered = this._patientCache.filter((p) => {
      if (p.name.toLowerCase().includes(term)) {
        return true;
      }
      if (matchPhone) {
        const phoneDigits = p.phone.replace(/\D/g, '');
        return phoneDigits.includes(digitTerm);
      }
      return false;
    });
    this._patients$.next(filtered);
  }

  /**
   * Adds a new patient to the current list of patients.
   *
   * @param patient - The patient to be added.
   */

  addPatients(patient: Patient): void {
    this._patientCache = [patient, ...this._patientCache];
    this._patients$.next([patient, ...this._patients$.getValue()]);
  }

  removePatient(patientId: string): void {
    this._patientCache = this._patientCache.filter((p) => p.id !== patientId);
    this._patients$.next(this._patients$.getValue().filter((p) => p.id !== patientId));
  }

  /**
   * Exact name+phone match against the loaded patient cache (not the filtered view).
   * Used by add-patient duplicate guard.
   */
  findExactPatientMatch(name: string, phone: string): Patient | undefined {
    return matchPatientByNameAndPhone(this._patientCache, name, phone);
  }

  /**
   * Loads the locations and updates the locations observable.
   */
  loadLocations(): void {
    this._locationsLoading.next(true);
    this.patientService.getLocations().subscribe({
      next: (data) => {
        this._locations$.next(data.result || []);
        this._locationsLoading.next(false);
      },
      error: () => {
        this._locationsLoading.next(false);
      },
    });
  }

  updateOrganization(args: UpdateProfileRequest, org: Organization): void {
    this.profileService
      .updateProfile(args)
      .pipe(take(1))
      .subscribe(() => {
        const profile = this._profile$.getValue() as Profile;
        this._profile$.next({ ...profile, currentOrg: org.id });
        sessionStorage.setItem('organizationId', org.id);
        this._currentOrganization$.next(org);
        this.loadPatients({ search: '' });
        this.loadLocations();
      });
  }
}
